"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BannerScope, GhostBanner } from "@/lib/ghost";
import { BannerRow } from "./BannerRow";

/**
 * Client-side site-wide banners, full-width across the viewport.
 *
 * Uses `position: fixed` + a `--banner-offset` CSS variable on <html> so
 * other layout chrome (sidebar top/height, main content padding-top) can
 * shift down to make room. The variable updates live via ResizeObserver —
 * dismissals, multi-banner stacks, and responsive wraps all push correctly.
 *
 * The fetched banners are cached in the TanStack Query store. SiteBannersClient
 * is mounted by both the (v2) and (public) layouts, so navigating across the
 * route-group boundary unmounts and remounts it; without a shared cache the
 * remount goes through `banners=[] → fetch → render` and the banner visibly
 * flickers on every cross-layout navigation. The query cache lives above both
 * layouts in `app/providers.tsx`, so cached results render synchronously on
 * remount and the flicker disappears after the first fetch per scope.
 */
const POLL_INTERVAL_MS = 60_000;
async function fetchBanners(scope?: BannerScope): Promise<GhostBanner[]> {
  const url = scope ? `/api/banners?scope=${encodeURIComponent(scope)}` : "/api/banners";
  const res = await fetch(url, {
    cache: "default"
  });
  if (!res.ok) return [];
  const data: {
    banners?: GhostBanner[];
  } = await res.json();
  return data.banners ?? [];
}
export function SiteBannersClient(t0) {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    scope
  } = t1;
  const ref = useRef(null);
  const t2 = scope ?? "default";
  let t3;
  if ($[2] !== t2) {
    t3 = ["site-banners", t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== scope) {
    t4 = () => fetchBanners(scope);
    $[4] = scope;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      refetchInterval: POLL_INTERVAL_MS,
      staleTime: POLL_INTERVAL_MS
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: t6
  } = useQuery(t5);
  let t7;
  if ($[9] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const banners = t7;
  let t8;
  if ($[11] !== banners.length) {
    t8 = () => {
      const root = document.documentElement;
      const el = ref.current;
      if (!el || banners.length === 0) {
        root.style.removeProperty("--banner-offset");
        return;
      }
      const sync = () => {
        const h = el.getBoundingClientRect().height;
        root.style.setProperty("--banner-offset", `${Math.round(h)}px`);
      };
      sync();
      const ro = new ResizeObserver(sync);
      ro.observe(el);
      return () => {
        ro.disconnect();
        root.style.removeProperty("--banner-offset");
      };
    };
    $[11] = banners.length;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== banners) {
    t9 = [banners];
    $[13] = banners;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  useEffect(t8, t9);
  if (banners.length === 0) {
    return null;
  }
  let t10;
  if ($[15] !== banners) {
    t10 = banners.map(_temp);
    $[15] = banners;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <div ref={ref} className="fixed left-0 right-0 top-0 z-[299]">{t10}</div>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}
function _temp(banner) {
  return <BannerRow key={banner.id} banner={banner} />;
}